import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { mlApi, Incident, RootCause, IncidentEvent } from '../../services/api/ml.api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';

export default function RootCausePage() {
  const { t } = useTranslation(['ml', 'common']);
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [rootCauses, setRootCauses] = useState<RootCause[]>([]);
  const [timeline, setTimeline] = useState<IncidentEvent[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [selectedAppId]);

  useEffect(() => {
    if (selectedIncident) {
      fetchIncidentDetails(selectedIncident.id);
    }
  }, [selectedIncident]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const incidentsData = await mlApi.getIncidents(undefined, selectedAppId);
      setIncidents(Array.isArray(incidentsData) ? incidentsData : []);
      if (Array.isArray(incidentsData) && incidentsData.length > 0) {
        setSelectedIncident(incidentsData[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load root cause data');
      console.error('Root cause fetch error:', err);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidentDetails = async (incidentId: string) => {
    try {
      setLoadingDetails(true);
      const [rootCausesData, timelineData] = await Promise.all([
        mlApi.getRootCauses(incidentId),
        mlApi.getIncidentTimeline(incidentId)
      ]);
      setRootCauses(Array.isArray(rootCausesData) ? rootCausesData : []);
      setTimeline(Array.isArray(timelineData) ? timelineData : []);
    } catch (err: any) {
      console.error('Failed to fetch incident details:', err);
      setRootCauses([]);
      setTimeline([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 dark:text-green-400';
    if (confidence >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High':
      case 'Critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'Low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'cause':
        return 'bg-red-500';
      case 'symptom':
        return 'bg-yellow-500';
      case 'correlated':
        return 'bg-blue-500';
      case 'remediation':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Generate correlation data for visualization
  const getCorrelationData = () => {
    if (!selectedIncident) return [];

    // Simulate metric correlation data during incident
    const startTime = new Date(selectedIncident.started_at).getTime();
    const endTime = selectedIncident.resolved_at ? new Date(selectedIncident.resolved_at).getTime() : Date.now();
    const duration = endTime - startTime;
    const points = 20;

    return Array.from({ length: points }, (_, i) => {
      const time = startTime + (duration / points) * i;
      const normalizedTime = i / points;

      // Simulate different metric patterns
      return {
        time: new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cpu: 40 + Math.sin(normalizedTime * Math.PI * 2) * 30 + (normalizedTime > 0.3 && normalizedTime < 0.7 ? 25 : 0),
        memory: 50 + Math.cos(normalizedTime * Math.PI * 2) * 20 + (normalizedTime > 0.4 && normalizedTime < 0.8 ? 20 : 0),
        errors: normalizedTime > 0.3 && normalizedTime < 0.7 ? 10 + Math.random() * 40 : Math.random() * 5,
        latency: 100 + (normalizedTime > 0.35 && normalizedTime < 0.75 ? 150 + Math.random() * 100 : Math.random() * 20),
      };
    });
  };

  // Generate AI insights about the incident
  const getIncidentInsights = () => {
    if (!selectedIncident || !rootCauses.length) return [];

    const insights = [];
    const topCause = rootCauses[0];

    // Severity-based insight
    if (selectedIncident.severity === 'critical' || selectedIncident.severity === 'high') {
      insights.push({
        icon: '🚨',
        title: 'High-Impact Incident',
        description: `This ${selectedIncident.severity} severity incident requires immediate attention. ${rootCauses.length} potential root causes identified.`,
        priority: 'critical',
      });
    }

    // Duration insight
    const duration = selectedIncident.resolved_at
      ? (new Date(selectedIncident.resolved_at).getTime() - new Date(selectedIncident.started_at).getTime()) / (1000 * 60)
      : (Date.now() - new Date(selectedIncident.started_at).getTime()) / (1000 * 60);

    if (duration > 60) {
      insights.push({
        icon: '⏱️',
        title: 'Extended Duration',
        description: `Incident lasted ${Math.round(duration)} minutes. Consider improving detection and response time.`,
        priority: 'high',
      });
    }

    // Confidence-based insight
    if (topCause && topCause.confidence_score >= 85) {
      insights.push({
        icon: '🎯',
        title: 'High Confidence Root Cause',
        description: `Primary cause identified with ${topCause.confidence_score.toFixed(0)}% confidence: ${topCause.cause_description}`,
        priority: 'info',
      });
    }

    // Pattern detection
    if (rootCauses.some(rc => rc.category?.toLowerCase().includes('resource') || rc.category?.toLowerCase().includes('capacity'))) {
      insights.push({
        icon: '📊',
        title: 'Resource Constraint Detected',
        description: 'Analysis suggests resource exhaustion as a contributing factor. Review capacity planning.',
        priority: 'medium',
      });
    }

    return insights;
  };

  // Generate actionable recommendations
  const getRecommendations = () => {
    if (!selectedIncident || !rootCauses.length) return [];

    const recommendations = [];
    const topCause = rootCauses[0];

    // Based on top root cause
    if (topCause) {
      if (topCause.category?.toLowerCase().includes('resource') || topCause.category?.toLowerCase().includes('capacity')) {
        recommendations.push({
          icon: '🚀',
          title: 'Scale Infrastructure',
          description: 'Add resources to prevent similar capacity issues',
          action: 'View Infrastructure',
          onClick: () => navigate('/infrastructure'),
        });

        recommendations.push({
          icon: '⚙️',
          title: 'Configure Auto-Scaling',
          description: 'Set up automatic scaling to handle load spikes',
          action: 'Configure',
          onClick: () => console.log('Configure auto-scaling'),
        });
      }

      if (topCause.category?.toLowerCase().includes('deployment') || topCause.category?.toLowerCase().includes('configuration')) {
        recommendations.push({
          icon: '🔄',
          title: 'Review Deployment Process',
          description: 'Implement rollback procedures and canary deployments',
          action: 'View Deployments',
          onClick: () => navigate('/apm/traces'),
        });
      }

      if (topCause.category?.toLowerCase().includes('dependency') || topCause.category?.toLowerCase().includes('external')) {
        recommendations.push({
          icon: '🔗',
          title: 'Strengthen Dependencies',
          description: 'Add circuit breakers and implement fallback mechanisms',
          action: 'View Services',
          onClick: () => navigate('/apm/services'),
        });
      }
    }

    // Generic recommendations
    recommendations.push({
      icon: '🔔',
      title: t('ml:rootCause.recommendations.createAlert.title'),
      description: t('ml:rootCause.recommendations.createAlert.description'),
      action: t('ml:rootCause.recommendations.createAlert.action'),
      onClick: () => navigate('/alerts'),
    });

    return recommendations.slice(0, 4);
  };

  const isAnalyzing = selectedIncident?.root_cause_analysis_status === 'analyzing';

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('ml:rootCause.loading')}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('ml:rootCause.title')}
          <span className="ml-3 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-1 text-sm font-semibold text-white">
            {t('ml:aiPowered')}
          </span>
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('ml:rootCause.subtitle')}</p>
      </div>

      {/* Incident Selector */}
      {incidents.length === 0 ? (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-gray-600 dark:text-gray-400">{t('ml:rootCause.noIncidents')}</p>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{t('ml:rootCause.selectIncident')}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {incidents.map((incident) => (
                <div
                  key={incident.id}
                  onClick={() => setSelectedIncident(incident)}
                  className={`cursor-pointer rounded-xl border p-4 transition-all hover:shadow-md ${
                    selectedIncident?.id === incident.id
                      ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/10'
                      : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
                  }`}
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white">{incident.title}</h3>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    {new Date(incident.started_at).toLocaleString()}
                  </p>
                  <span
                    className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                      incident.root_cause_analysis_status === 'analyzing'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        : incident.root_cause_analysis_status === 'completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                  >
                    {incident.root_cause_analysis_status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {loadingDetails ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600"></div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{t('ml:rootCause.loadingDetails')}</p>
            </div>
          ) : isAnalyzing ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600"></div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{t('ml:rootCause.analyzing')}</p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('ml:rootCause.analyzingDetails')}
              </p>
            </div>
          ) : (
            <>
              {/* Root Causes */}
              <div className="mb-8">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {t('ml:rootCause.possibleCauses')}
                </h2>
                {rootCauses.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <p className="text-gray-600 dark:text-gray-400">{t('ml:rootCause.noRootCauses')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {rootCauses.map((rc, index) => (
                      <div
                        key={rc.id}
                        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                                #{index + 1}
                              </span>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {rc.cause_description}
                              </h3>
                              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getImpactColor(rc.impact_level)}`}>
                                {t(`ml:rootCause.impact.${rc.impact_level.toLowerCase()}`, rc.impact_level + ' Impact')}
                              </span>
                              {rc.category && (
                                <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                                  {rc.category}
                                </span>
                              )}
                            </div>

                            <div className="mt-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('ml:rootCause.confidence')}:</span>
                                <span className={`text-xl font-bold ${getConfidenceColor(rc.confidence_score)}`}>
                                  {rc.confidence_score.toFixed(1)}%
                                </span>
                                <div className="flex-1">
                                  <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-600"
                                      style={{ width: `${rc.confidence_score}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {rc.correlated_events && rc.correlated_events.length > 0 && (
                              <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ml:rootCause.correlatedEvents')}:</p>
                                <ul className="mt-2 space-y-1">
                                  {rc.correlated_events.map((event, i) => (
                                    <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                                      <span className="mr-2">•</span>
                                      <span>{event}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {rc.affected_components && rc.affected_components.length > 0 && (
                              <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ml:rootCause.affectedComponents')}:</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {rc.affected_components.map((component) => (
                                    <span
                                      key={component}
                                      className="rounded-lg bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    >
                                      {component}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {rc.recommendation && (
                              <div className="mt-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/10">
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-400">
                                  {t('ml:rootCause.recommendation')}:
                                </p>
                                <p className="mt-1 text-sm text-blue-700 dark:text-blue-500">{rc.recommendation}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Timeline */}
              {timeline.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <h2 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{t('ml:rootCause.eventTimeline')}</h2>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                    {/* Timeline events */}
                    <div className="space-y-6">
                      {timeline.map((item) => (
                        <div key={item.id} className="relative flex items-start">
                          {/* Timeline dot */}
                          <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${getEventTypeColor(item.event_type)}`}>
                            <div className="h-3 w-3 rounded-full bg-white"></div>
                          </div>

                          {/* Event content */}
                          <div className="ml-4 flex-1">
                            <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                              {new Date(item.occurred_at).toLocaleTimeString()}
                            </p>
                            <p className="mt-1 font-medium text-gray-900 dark:text-white">{item.event_description}</p>
                            <span className="mt-1 inline-flex text-xs capitalize text-gray-500 dark:text-gray-500">
                              {item.event_type}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="mt-6 flex flex-wrap gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                    {['cause', 'symptom', 'correlated', 'remediation'].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <div className={`h-3 w-3 rounded-full ${getEventTypeColor(type)}`}></div>
                        <span className="text-sm capitalize text-gray-600 dark:text-gray-400">{t(`ml:rootCause.eventTypes.${type}`, type)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metric Correlations */}
              {selectedIncident && (
                <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-6 flex items-center space-x-2">
                    <span className="text-2xl">📊</span>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('rootCause.metricCorrelations.title')}
                    </h2>
                    <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                      {t('rootCause.metricCorrelations.aiAnalysis')}
                    </span>
                  </div>

                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    {t('rootCause.metricCorrelations.description')}
                  </p>

                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getCorrelationData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="cpu" stroke="#ef4444" name="CPU %" strokeWidth={2} />
                      <Line type="monotone" dataKey="memory" stroke="#3b82f6" name="Memory %" strokeWidth={2} />
                      <Line type="monotone" dataKey="errors" stroke="#f59e0b" name="Error Rate" strokeWidth={2} />
                      <Line type="monotone" dataKey="latency" stroke="#8b5cf6" name="Latency (ms)" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>

                  <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">CPU Usage</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Spike during incident</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/10">
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Memory</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Elevated levels</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-900/10">
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Error Rate</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Correlated increase</p>
                    </div>
                    <div className="rounded-lg bg-purple-50 p-3 dark:bg-purple-900/10">
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-purple-500"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Latency</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Performance degradation</p>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {selectedIncident && getIncidentInsights().length > 0 && (
                <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-4 flex items-center space-x-2">
                    <span className="text-2xl">🤖</span>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI-Powered Insights</h2>
                  </div>
                  <div className="space-y-3">
                    {getIncidentInsights().map((insight, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-4 ${
                          insight.priority === 'critical'
                            ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800'
                            : insight.priority === 'high'
                            ? 'bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800'
                            : 'bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">{insight.icon}</span>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{insight.title}</h4>
                            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actionable Recommendations */}
              {selectedIncident && getRecommendations().length > 0 && (
                <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-4 flex items-center space-x-2">
                    <span className="text-2xl">💡</span>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recommended Actions</h2>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {getRecommendations().map((rec, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-4 transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">{rec.icon}</span>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{rec.title}</h4>
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{rec.description}</p>
                            <button
                              onClick={rec.onClick}
                              className="mt-3 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              {rec.action}
                              <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </MainLayout>
  );
}
